import React from 'react';
import { Box, BoxProps } from '@mui/material';

interface Props extends BoxProps {
  setCount?: (count: number) => void;
  onFinish?: () => void;
}

export const SwipableCardStack: React.FC<Props> = ({
  children,
  onFinish,
  setCount,
  ...props
}) => {
  const cards = React.Children.toArray(children);
  const [hiddenIndexes, setHiddenIndexes] = React.useState<Set<number>>(new Set());
  const visibleIndexes = cards.map((_, i) => i).filter(i => !hiddenIndexes.has(i));
  const topIndex = visibleIndexes[0]; // the top/front-most card

  // Number of visible cards
  const visibleCount = cards.length - hiddenIndexes.size;

  const handleSwiped = (index: number) => {
    setHiddenIndexes(prev => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  };

  // Update count when stack changes
  React.useEffect(() => {
    setCount && setCount(visibleCount);
  }, [visibleCount, setCount]);

  // Call onFinish when all cards are swiped
  React.useEffect(() => {
    if (hiddenIndexes.size === cards.length && typeof onFinish === 'function') {
      onFinish();
    }
  }, [hiddenIndexes.size, cards.length, onFinish]);

  return (
    <Box position="relative" height="100%" {...props}>
      {cards.map((card, i) => {
        // @ts-ignore
        const key = card.props.id || i;
        const isFront = i === topIndex;
        return (
          <Box
            key={key}
            position="absolute"
            top={0}
            right={0}
            bottom={0}
            left={0}
            style={{
              zIndex: cards.length - i,
              display: hiddenIndexes.has(i) ? 'none' : 'block',
              pointerEvents: hiddenIndexes.has(i) ? 'none' : 'auto',
              willChange: 'display',
            }}
          >
            {React.cloneElement(card as React.ReactElement<any>, {
              onSwiped: () => handleSwiped(i),
              isFront,
            })}
          </Box>
        );
      })}
    </Box>
  );
};